<template>
  <CCard class="calendar-session-card">
    <CCardHeader class="d-flex justify-content-between align-items-center mt-3">
      <CButton
        color="primary"
        class="mr-2"
        @click="goBack"
      >
        ← Back
      </CButton>
      <h2 class="mb-0">
        {{ isEditing ? 'Edit Session' : 'New Session' }}
      </h2>
      <div style="width: 120px; display: flex; justify-content: flex-end; gap: 8px;">
        <CButton 
          v-if="isEditing" 
          color="danger" 
          @click="deleteSession"
        >
          Delete
        </CButton>
      </div>
    </CCardHeader>

    <div
      v-if="calendarEntry.session"
      class="instructor-header"
    >
      <div
        v-if="sessionImageLoaded"
        class="header-bg"
        :class="{ 'bg-missing': !sessionImageLoaded }"
        :style="{ backgroundImage: 'url(' + sessionImage + ')' }"
      />
      <div
        v-else
        class="header-bg bg-missing"
      />

      <!-- X dentro del header, arriba-derecha -->
      <button
        class="session-remove-x"
        title="Remove session"
        @click="removeAssignedSession"
      >
        ✕
      </button>

      <div class="instructor-info">
        <img
          v-if="instructorImageLoaded"
          :src="instructorImage"
          class="instructor-avatar"
          alt="Instructor avatar"
          @error="onInstructorImgError"
        >
        <div
          v-else
          class="instructor-avatar avatar-fallback"
        />

        <div class="text-zone">
          <h3 class="session-title">
            {{ sessionName }}
          </h3>
          <p class="instructor-name">
            {{ instructorName }}
          </p>
        </div>
      </div>
    </div>

    <CCardBody>
      <div class="form-group mb-4">
        <label>Start At</label>
        <input
          v-model="calendarEntry.start_at_local"
          type="datetime-local"
          class="form-control"
        >
      </div>

      <div class="form-group mb-4">
        <div
          v-if="calendarEntry.session == null"
          class="d-inline-flex align-items-center"
        >
          <CButton
            color="primary"
            @click="showSessionModal = true"
          >
            Select Session
          </CButton>
        </div>
      </div>

      <SessionPickerModal
        :show="showSessionModal"
        :selected-session="calendarEntry.session"
        @update:show="showSessionModal = $event"
        @selected="handleSessionSelected"
      />
      <div class="form-group mt-4">
        <CButton
          color="primary"
          @click="saveSession"
        >
          {{ isEditing ? 'Update' : 'Create' }}
        </CButton>
      </div>
    </CCardBody>
  </CCard>
</template>

<script>

import SessionPickerModal from '@/components/SessionPickerModal'

export default {
  name: 'CalendarSessionForm',
  components: { SessionPickerModal },
  data() {
    return {
      sessionImageLoaded: false,
      instructorImageLoaded: false,
      sessionImageCheckTimeout: 4000,

      calendarEntry: {
        start_at: '',
        start_at_local: '',
        session: null
      },
      defaultInstructorImage: '/img/default-instructor.jpg',
      defaultSessionImage: '/img/session-placeholder.jpg',
      showSessionModal: false,
      isEditing: false,
      calendarSessionId: null,
    }
  },
  computed: {
    sessionName() {
      const s = this.calendarEntry.session
      if (!s) return 'Select a session'
      return s.name || s.title || (s.session && (s.session.name || s.session.title)) || 'Untitled'
    },

    instructorName() {
      const s = this.calendarEntry.session
      if (!s) return '—'

      if (Array.isArray(s.instructor_name) && s.instructor_name.length && s.instructor_name[0].name) {
        return s.instructor_name[0].name
      }
      if (Array.isArray(s.instructors) && s.instructors.length && s.instructors[0].name) {
        return s.instructors[0].name
      }
      if (s.instructor && s.instructor.name) {
        return s.instructor.name
      }
      return '—'
    },

    sessionImage() {
      const s = this.calendarEntry.session
      if (!s) return this.defaultSessionImage

      if (s.image) return s.image
      if (s.image_url) return s.image_url
      if (s.session && (s.session.image || s.session.image_url)) return s.session.image || s.session.image_url
      return this.defaultSessionImage
    },

    instructorImage() {
      const s = this.calendarEntry.session
      if (!s) return this.defaultInstructorImage

      if (Array.isArray(s.instructor_name) && s.instructor_name.length) {
        const first = s.instructor_name[0]
        if (first.picture_url) return first.picture_url
        if (first.picture) return first.picture
      }

      if (Array.isArray(s.instructors) && s.instructors.length) {
        const first = s.instructors[0]
        if (first.picture_url) return first.picture_url
        if (first.picture) return first.picture
      }

      if (s.instructor) {
        if (s.instructor.picture_url) return s.instructor.picture_url
        if (s.instructor.image) return s.instructor.image
      }

      if (s.image) return s.image

      return this.defaultInstructorImage
    }
  },

  watch: {
    'calendarEntry.session': {
      handler(newVal) {
        if (newVal) {
          this.validateImagesForCurrentSession()
        } else {
          this.sessionImageLoaded = false
          this.instructorImageLoaded = false
        }
      },
      immediate: false
    }
  },

  mounted() {
    const { calendarSessionId, date } = this.$route.query;

    if (calendarSessionId) {
      this.isEditing = true
      this.calendarSessionId = calendarSessionId
      this.loadCalendarEntry(calendarSessionId)
    } else if (date) {
      this.isEditing = false
      this.calendarEntry.start_at = date
      this.calendarEntry.start_at_local = this.utcToLocalInput(date)
    }
  },

  methods: {
    goBack() {
      this.$router.back()
    },

    checkImageUrl(url, timeout = this.sessionImageCheckTimeout) {
      return new Promise(resolve => {
        if (!url) return resolve(false)

        let settled = false
        const img = new Image()

        const clean = (val) => {
          if (settled) return
          settled = true
          img.onload = img.onerror = null
          try { clearTimeout(timer) } catch(e){}
          resolve(!!val)
        }

        img.onload = () => clean(true)
        img.onerror = () => clean(false)

        setTimeout(() => { img.src = url }, 0)

        const timer = setTimeout(() => clean(false), timeout)
      })
    },

    async validateImagesForCurrentSession() {
      const sUrl = this.sessionImage
      const iUrl = this.instructorImage
      const [sOk, iOk] = await Promise.all([
        this.checkImageUrl(sUrl),
        this.checkImageUrl(iUrl)
      ])

      this.sessionImageLoaded = !!sOk
      this.instructorImageLoaded = !!iOk
    },

    onInstructorImgError() {
      this.instructorImageLoaded = false
    },

    async loadCalendarEntry(id) {
      try {
        const resp = await this.$http.get(`gym/calendar/${id}`)
        const entry = resp.data.calendar_entry

        this.calendarEntry.start_at = entry.start_at
        this.calendarEntry.start_at_local = this.utcToLocalInput(entry.start_at)
        this.calendarEntry.session = entry.session || entry
        await this.validateImagesForCurrentSession()
      } catch (err) {
        console.error('Error loading calendar entry:', err)
      }
    },

    utcToLocalInput(utcStr) {
      if (!utcStr) return ''
      const utcDate = new Date(utcStr + 'Z') // forzamos UTC
      const tzOffset = utcDate.getTimezoneOffset() * 60000
      const localDate = new Date(utcDate - tzOffset)
      return localDate.toISOString().slice(0,16)
    },

    localInputToUTC(localStr) {
      if (!localStr) return ''
      const localDate = new Date(localStr)
      const utcYear = localDate.getUTCFullYear()
      const utcMonth = String(localDate.getUTCMonth() + 1).padStart(2, '0')
      const utcDay = String(localDate.getUTCDate()).padStart(2, '0')
      const utcHours = String(localDate.getUTCHours()).padStart(2, '0')
      const utcMinutes = String(localDate.getUTCMinutes()).padStart(2, '0')

      return `${utcYear}-${utcMonth}-${utcDay}T${utcHours}:${utcMinutes}Z`
    },

    handleSessionSelected(session) {
      this.calendarEntry.session = session
      this.showSessionModal = false
      this.validateImagesForCurrentSession()
    },

    removeAssignedSession() {
      this.calendarEntry.session = null
      this.sessionImageLoaded = false
      this.instructorImageLoaded = false
    },

    async deleteSession() {
      if (!confirm('Are you sure you want to delete this session?')) return;

      try {
        await this.$http.delete(`gym/calendar/${this.calendarSessionId}`);
        this.$toast.success('Session deleted successfully!');
        this.$router.push({ name: 'Calendar' });
      } catch (err) {
        const msg = err.response && err.response.data && err.response.data.error
          ? err.response.data.error
          : 'Error deleting session';
        this.$toast.error(msg);
      }
    },

    async saveSession() {
      if (!this.calendarEntry.session) {
        this.$toast.error('Please select a session!');
        return
      }

      const payload = {
        start_at: this.localInputToUTC(this.calendarEntry.start_at_local),
        session_id: this.calendarEntry.session.id
      }

      try {
        if (this.isEditing) {
          await this.$http.put(`gym/calendar/${this.calendarSessionId}/update`, payload)
        } else {
          await this.$http.post(`gym/calendar`, payload)
        }
        this.$toast.success('Session added to calendar!');
        this.$router.push({ name: 'Calendar' })
      } catch (err) {
        const msg = err.response && err.response.data && err.response.data.error
          ? err.response.data.error
          : 'Error saving session'
        this.$toast.error(msg)
      }
    }
  }
}
</script>

<style scoped>

.instructor-header {
  position: relative;
  width: 100%;
  height: 220px;
  border-radius: 0px;
  overflow: hidden;
  margin-bottom: 14px;
}

.header-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  filter: blur(6px) brightness(0.55);
  transform: scale(1.05);
  z-index: 10;
}

.header-bg.bg-missing {
  background: #000 !important;
  filter: none !important;
  transform: none;
}


.session-remove-x {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 40;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: rgba(255,255,255,0.95);
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0,0,0,0.12);
}

.instructor-info {
  position: relative;
  z-index: 30;
  display: flex;
  align-items: center;
  height: 100%;
  padding-left: 20px;
  gap: 16px;
}

.instructor-avatar {
  width: 90px;
  height: 90px;
  border-radius: 100px;
  object-fit: cover;
  border: 3px solid rgba(255,255,255,0.95);
  box-shadow: 0 6px 18px rgba(0,0,0,0.25);
  background: transparent;
}

/* avatar fallback: blanco (círculo) */
.avatar-fallback {
  width: 90px;
  height: 90px;
  border-radius: 50%;
  background: #fff;
  border: 3px solid rgba(255,255,255,0.95);
  box-shadow: 0 6px 18px rgba(0,0,0,0.12);
}

.text-zone {
  display: flex;
  flex-direction: column;
  color: white;
}

.session-title {
  margin: 0;
  color: white;
  font-size: 20px;
  font-weight: 700;
}

.instructor-name {
  margin: 4px 0 0 0;
  color: white;
  font-size: 14px;
  opacity: 0.95;
}

/* Otros estilos menores */
.session-tag {
  background-color: #1282f3;
  border-radius: 20px;
  padding: 4px 10px;
  font-size: 14px;
  margin: 8px;
}

.close-tag {
  background: none;
  border: none;
  font-size: 16px;
  line-height: 1;
  margin-left: 8px;
  cursor: pointer;
}
</style>
